import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import type { BoardSnapshotResponse, ParticipantRole } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { Loading } from "../../lib/loading.tsx";

/**
 * Excalidraw ленивым чанком, а сама страница нет. Так запрос снимка и загрузка
 * доски идут параллельно: на 128 кбит/с последовательность «сначала чанк,
 * потом запрос» стоила бы лишних секунд на ровном месте.
 */
const ArchivedBoard = lazy(() =>
  import("./ArchivedBoard.tsx").then((m) => ({ default: m.ArchivedBoard })),
);

/** Снимка ещё нет: API уже запустил ремонтный проход, ждём и спрашиваем снова. */
const RETRY_MS = 3000;

/**
 * Экран архива урока.
 *
 * `409 snapshot_pending` это не ошибка, а нормальное состояние: снимок
 * снимается при выгрузке документа, по кнопке «завершить урок» и раз в пять
 * минут. Если урок только что закрыли, документ может ещё выгружаться, и API
 * в этот момент асинхронно дёргает ремонтный проход. Наше дело подождать и
 * перезапросить, а не показывать «ошибка 409».
 */
export function ArchivePage() {
  const { lessonId = "" } = useParams();
  const [snapshot, setSnapshot] = useState<BoardSnapshotResponse | null>(null);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  /**
   * Роль в уроке. От неё зависит только вид упражнений (попытки и пометка «это
   * ваш эталон»), права считает сервер, поэтому безопасное умолчание это
   * «ученик»: ошибись мы здесь, и лишнего всё равно не покажется, потому что
   * лишнего не приедет.
   */
  const [role, setRole] = useState<ParticipantRole>("student");

  useEffect(() => {
    let alive = true;
    // Параллельно снимку, а не после него: на 128 кбит/с последовательность
    // из двух запросов стоила бы лишней секунды на ровном месте
    void api
      .getLesson(lessonId)
      .then((lesson) => {
        if (alive && lesson.myRole !== undefined) setRole(lesson.myRole);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [lessonId]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const snap = await api.getBoardSnapshot(lessonId);
        if (!alive) return;
        setSnapshot(snap);
        setPending(false);
      } catch (e) {
        if (!alive) return;
        if (e instanceof api.ApiError && e.code === "snapshot_pending") {
          setPending(true);
          timer.current = window.setTimeout(() => void load(), RETRY_MS);
          return;
        }
        setErr(
          e instanceof api.ApiError && e.status === 404
            ? "Урок не найден или доступ к архиву закрыт"
            : e instanceof Error
              ? e.message
              : String(e),
        );
      }
    };

    void load();
    return () => {
      alive = false;
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, [lessonId]);

  if (err) {
    return (
      <main className="lobby">
        <h1>Архив</h1>
        <p className="err">{err}</p>
        {/*
          Единственный выход с экрана отказа: разбора не будет, и делать здесь
          больше нечего. На этом экране акцент честный ровно потому, что
          соревноваться ему не с чем
        */}
        <Link className="btn btn--primary" to="/archive">
          К списку занятий
        </Link>
      </main>
    );
  }

  if (!snapshot) {
    return (
      <main className="lobby">
        <h1>Архив</h1>
        {/* Слово своё только у «готовится»: это ДРУГОЙ факт (снимок доски
            собирается на сервере), а не второе название ожидания */}
        {pending ? <Loading text="Архив готовится, обновим сами…" /> : <Loading />}
      </main>
    );
  }

  return (
    <div className="room-shell">
      <div className="archive">
        {/* Обёртки .board здесь нет намеренно: со слайдами доска это не
            только холст, но и полоса навигации, и раскладку между ними
            задаёт сама доска */}
        <Suspense fallback={<Loading />}>
          <ArchivedBoard snapshot={snapshot} role={role} />
        </Suspense>
        <div className="archive__bar">
          {/*
            Ведёт в раздел «Архив», а не на корень. Сюда приходят из трёх мест
            (кабинет, уроки по заготовке, список архива), и одна кнопка не может
            вернуть в то, откуда пришли, не заведя состояния. Раз выбирать одно
            место, то это список проведённых занятий: он показывает соседние
            уроки того же ученика, то есть ровно то, зачем архив и открывали.
            Кнопка «назад» браузера при этом работает как раньше.

            Тихий вес: экран открыт ради доски, а полоса над ней это хром.
            Акцента здесь нет ни у чего, потому что действий над архивом нет
            вовсе, он только для чтения
          */}
          <Link className="btn btn--quiet" to="/archive">
            Назад
          </Link>
          <span className="muted">
            {snapshot.elementCount} элементов · снимок{" "}
            {new Date(snapshot.takenAt).toLocaleString("ru-RU")}
          </span>
        </div>
      </div>
    </div>
  );
}
